// components/GlobalSupabaseFetchConfig.tsx
import { useEffect, useMemo } from 'react';
import { useSupabaseFromContext } from './SupabaseClientContext';
import { Session, User } from '@supabase/supabase-js';

// Define a global type for the window object

export interface SupabaseFetchConfig {
  baseUrl: string | null;
  headers: {
    "apikey": string | null;
    "Content-Type": "application/json";
    "Authorization"?: string;
  };
  isReady: boolean;
  usingServiceKeyForDev: boolean;
  currentUser: User | null;
  currentUserEmail: string | null;
  getLatestConfig: () => Promise<SupabaseFetchConfig>;
}

// This component is now a "fire-and-forget" initializer. It has no internal state.
export function GlobalSupabaseFetchConfig(): null {
  const supabaseClient = useSupabaseFromContext();

  // Use useMemo for stable references to env variables
  const env = useMemo(() => ({
    baseUrl: process.env.NEXT_PUBLIC_SUPABASE_URL || null,
    anonKey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || null,
    serviceKeyForDev: process.env.SUPABASE_SERVICE_ROLE_KEY || null,
  }), []);

  useEffect(() => {
    console.log("[GlobalSupabaseFetchConfig] Initializing and setting up listeners.");

    const updateGlobalConfig = (session: Session | null) => {
      console.log("[GlobalSupabaseFetchConfig] Updating global config object.");

      const isDev = process.env.NODE_ENV === 'development';
      const inIframe = typeof window !== 'undefined' && window.self !== window.top;
      const onLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost');
      
      const shouldUseServiceKey = isDev && inIframe && onLocalhost && !!env.serviceKeyForDev;

      let authorizationHeader: string | undefined;
      let activeUser: User | null = null;

      if (shouldUseServiceKey) {
        authorizationHeader = `Bearer ${env.serviceKeyForDev}`;
        activeUser = null; // When using service key, there is no "current user"
      } else if (session?.access_token) {
        authorizationHeader = `Bearer ${session.access_token}`;
        activeUser = session.user;
      }

      const getLatestConfig = async (): Promise<SupabaseFetchConfig> => {
          // This function is now much simpler. It just gets the latest session.
          const { data: { session: latestSession } } = await supabaseClient.auth.getSession();
          // We call the same update function to regenerate the config based on the latest session.
          return updateGlobalConfig(latestSession);
      };
      
      const newConfig: SupabaseFetchConfig = {
        baseUrl: env.baseUrl,
        headers: {
          "apikey": env.anonKey,
          "Content-Type": "application/json",
          ...(authorizationHeader && { "Authorization": authorizationHeader }),
        },
        isReady: !!(env.baseUrl && env.anonKey && (shouldUseServiceKey || session)),
        usingServiceKeyForDev: shouldUseServiceKey,
        currentUser: activeUser,
        currentUserEmail: activeUser?.email || null,
        getLatestConfig: getLatestConfig
      };

      window._myAppGlobalFetchConfig = newConfig;
      // Return the config for internal use
      return newConfig;
    };

    // 1. Get the initial session and perform the first update.
    supabaseClient.auth.getSession().then(({ data: { session } }) => {
      console.log("[GlobalSupabaseFetchConfig] Initial session fetched.");
      updateGlobalConfig(session);
    });

    // 2. Set up a listener for all future auth changes.
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
      (_event, newSession) => {
        console.log("[GlobalSupabaseFetchConfig] Auth state changed.");
        updateGlobalConfig(newSession);
      }
    );

    // 3. The cleanup function unsubscribes from the listener.
    return () => {
      console.log("[GlobalSupabaseFetchConfig] Cleaning up auth listener.");
      subscription?.unsubscribe();
    };
  // The dependency array is stable. This effect runs ONLY ONCE when the component mounts.
  }, [supabaseClient, env]);

  return null;
}